/**
 * "Loved by Couples Everywhere" (V26): a slowly scrolling list of real, verified client reviews.
 *
 * Reads public.reviews (is_verified, rating >= 4, with a comment) joined to the reviewer's name, the same rows the
 * previous homepage showed. No review, name or location is invented: the reviews table has no location, so the
 * card says "Verified client". The three figures beside the heading are static marketing copy supplied by
 * Love & Photos (Trera, 21 Sep 2026: "1000+ reviews and 4.8 rating"), not computed from this table.
 */
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@lib/supabase'

export function toCards(rows = [], limit = 12) {
  return rows
    .filter(r => r?.comment && String(r.comment).trim() !== '')
    .slice(0, limit)
    .map(r => ({ id: r.id, text: String(r.comment).trim(), name: r.users?.full_name?.trim() || 'Verified client' }))
}

export default function ReviewStream() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const { data: recent, error } = await supabase
          .from('reviews')
          .select('id, comment, rating, created_at, users:reviewer_id (full_name)')
          .eq('is_verified', true)
          .gte('rating', 4)
          .not('comment', 'is', null)
          .order('created_at', { ascending: false })
          .limit(24)
        if (cancelled) return
        if (error) console.error('[reviews] load failed:', error)
        setRows(recent || [])
      } catch (err) {
        if (!cancelled) console.error('[reviews] load failed:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const cards = useMemo(() => toCards(rows), [rows])
  // The stream loops by scrolling half its height, so the list is rendered twice.
  const looped = cards.length ? [...cards, ...cards] : []

  return (
    <section className="reviews review-wall-section" id="about" aria-labelledby="reviews-heading">
      <div className="review-stream-wrap" aria-label="Verified client reviews">
        <div className="review-fade review-fade-top" />
        <div className="review-fade review-fade-bottom" />
        {looped.length > 0 ? (
          <div className="review-stream">
            {looped.map((c, i) => (
              <div className="review-card" key={`${c.id}-${i}`} aria-hidden={i >= cards.length}>
                <p>“{c.text}”</p>
                <b>{c.name}</b>
                <span>Verified client</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="review-empty" style={{ padding: '40px 0' }}>
            {loading ? 'Loading reviews…' : 'Verified client reviews appear here as couples share them.'}
          </p>
        )}
      </div>
      <div className="review-main">
        <div>
          <div className="eyebrow">Kind words</div>
          <h2 id="reviews-heading" className="serif">Loved by Couples<br />Everywhere</h2>
          <p className="verified-script">Real reviews, from real couples — 100% verified.</p>
          <div className="stats">
            <div className="stat"><strong>1,000+</strong><span>reviews</span></div>
            <div className="stat"><strong>4.8★</strong><span>average rating</span></div>
            <div className="stat"><strong>Nationwide</strong><span>&amp; beyond</span></div>
          </div>
        </div>
      </div>
    </section>
  )
}
